import React from 'react';
import { BillingConsumptionContextProvider } from '../contexts/Billing/BillingConsumptionContext';
import { BillingMetricsContextProvider, useBillingMetricsContext } from '../contexts/Billing/BillingMetricsContext';
import Revenue from '../views/Revenue';
import DailyActiveUsers from '../views/DailyActiveUsers';
import DailyEngagedUsers from '../views/DailyEngagedUsers';
import OldSessionConsumption from '../views/SessionConsumption';
import OldAgentConsumption from '../views/AgentConsumption';

import MiuConsumption from '../views/Billing/MiuConsumption';
import MeuConsumption from '../views/Billing/MeuConsumption';
import DauConsumption from '../views/Billing/DailyActiveUsers';
import DeuConsumption from '../views/Billing/DailyEngagedUsers';
import SessionConsumption from '../views/Billing/SessionConsumption';
import AgentConsumption from '../views/Billing/AgentConsumption';
import AdvancedDeuConsumption from '../views/Billing/AdvancedDailyEngagedUser';
import AiTokenConsumption from '../views/Billing/AiTokenConsumption';
import BillingConsumption from '../views/Billing';
import { useTenant } from '../contexts/TenantContext';
import { useCommon } from '../contexts/CommonContext';

import { BillingMetrics } from '../shared/BillingMetrics';

import { PrivateRoute } from '../components/PrivateRoute';

import {
  DATA_CONSUMPTION,
  DAILY_ACTIVE_USERS_CONSUMPTION,
  DAILY_ENGAGED_USERS_CONSUMPTION,
  AGENT_CONSUMPTION,
  SESSION_CONSUMPTION,
  TENANT_CONSUMPTION,
  MONTHLY_ENGAGED_USERS_CONSUMPTION,
  ADVANCED_DEU_ENABLED,
  AI_TOKEN_ENABLED,
} from '../shared/FeatureToggleKeys';

import {
  REPORT_OVERVIEW_CONSUMPTION_OPEN,
  REPORT_DAU_CONSUMPTION_OPEN,
  REPORT_DEU_CONSUMPTION_OPEN,
  REPORT_AGENT_CONSUMPTION_OPEN,
  REPORT_SESSION_CONSUMPTION_OPEN,
  REPORT_REVENUE_CONSUMPTION_OPEN,
  REPORT_MEU_CONSUMPTION_OPEN,
  REPORT_DAU_CONSUMPTION_PER_CHATBOT_OPEN,
  REPORT_DEU_CONSUMPTION_PER_CHATBOT_OPEN,
  REPORT_DAU_CONSUMPTION_PER_DATE_OPEN,
  REPORT_DEU_CONSUMPTION_PER_DATE_OPEN,
  REPORT_AGENT_CONSUMPTION_LIST_OPEN,
  REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_OPEN,
  REPORT_SESSION_CONSUMPTION_PER_DATE_OPEN,
  REPORT_REVENUE_CONSUMPTION_PER_CHATBOT_OPEN,
  REPORT_MEU_CONSUMPTION_PER_CHATBOT_OPEN,
  REPORT_ADVANCED_DEU_CONSUMPTION_OPEN,
  REPORT_AI_TOKEN_CONSUMPTION_OPEN,
} from '../shared/SegmentEvents';
import { DauConsumptionContextProvider, useDauConsumptionContext } from '../contexts/Billing/DauConsumptionContext';
import { DeuConsumptionContextProvider, useDeuConsumptionContext } from '../contexts/Billing/DeuConsumptionContext';
import { AdvancedDeuConsumptionContextProvider } from '../contexts/Billing/AdvancedDeuConsumptionContext';
import {
  AgentConsumptionContextProvider,
  useAgentConsumptionContext,
} from '../contexts/Billing/AgentConsumptionContext';
import {
  SessionConsumptionContextProvider,
  useSessionConsumptionContext,
} from '../contexts/Billing/SessionConsumptionContext';
import { MiuConsumptionContextProvider, useMiuConsumptionContext } from '../contexts/Billing/MiuConsumptionContext';
import { MeuConsumptionContextProvider, useMeuConsumptionContext } from '../contexts/Billing/MeuConsumptionContext';
import { BillingPlanUsageContextProvider } from '../contexts/Billing/BillingPlanUsageContext';
import { AiTokenConsumptionDataContextProvider } from '../contexts/Billing/AiTokenConsumptionDataContext';
import { accessPermission } from '../services/PermissionService';

export const billingRoutes = {
  dataConsumption: '/data-consumption',
  agentConsumption: '/agent-consumption',
  dauConsumption: '/dau-consumption',
  deuConsumption: '/deu-consumption',
  sessionConsumption: '/session-consumption',
  mauConsumption: '/mau-consumption',
  meuConsumption: '/meu-consumption',
  advancedDeuConsumption: '/advanced-deu-consumption',
  aiTokenConsumption: '/ai-tokens-consumption',

  revenue: '/revenue',
  dailyActiveUsers: '/daily-active-users',
  dailyEngagedUsers: '/daily-engaged-users',
  oldSessionConsumption: '/old-session-consumption',
  oldAgentConsumption: '/old-agent-consumption',
};

export const billingSubRoutes = {
  advancedDeuConsumptionMonthly: billingRoutes.advancedDeuConsumption + '/monthly',
  advancedDeuConsumptionDaily: billingRoutes.advancedDeuConsumption + '/daily',
  agentConsumptionMonthly: billingRoutes.agentConsumption + '/monthly',
  dauConsumptionMonthly: billingRoutes.dauConsumption + '/monthly',
  dauConsumptionDaily: billingRoutes.dauConsumption + '/daily',
  deuConsumptionMonthly: billingRoutes.deuConsumption + '/monthly',
  deuConsumptionDaily: billingRoutes.deuConsumption + '/daily',
  sessionConsumptionMonthly: billingRoutes.sessionConsumption + '/monthly',
  sessionConsumptionDaily: billingRoutes.sessionConsumption + '/daily',
  mauConsumptionChatbots: billingRoutes.mauConsumption + '/chatbots',
  mauConsumptionDaily: billingRoutes.mauConsumption + '/daily',
  meuConsumptionChatbots: billingRoutes.meuConsumption + '/chatbots',
  meuConsumptionDaily: billingRoutes.meuConsumption + '/daily',

  revenueChatbots: billingRoutes.revenue + '/chatbots',
  dailyActiveUsersMonthly: billingRoutes.dailyActiveUsers + '/monthly',
  dailyActiveUsersDaily: billingRoutes.dailyActiveUsers + '/daily',
  dailyEngagedUsersMonthly: billingRoutes.dailyEngagedUsers + '/monthly',
  dailyEngagedUsersDaily: billingRoutes.dailyEngagedUsers + '/daily',
  oldSessionConsumptionMonthly: billingRoutes.oldSessionConsumption + '/monthly',
  oldSessionConsumptionDaily: billingRoutes.oldSessionConsumption + '/daily',
  oldAgentConsumptionMonthly: billingRoutes.oldAgentConsumption + '/monthly',
};

export const billingRoutesTracks = [
  {
    path: billingRoutes.dataConsumption,
    segmentEvent: REPORT_OVERVIEW_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.dauConsumption,
    segmentEvent: REPORT_DAU_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.deuConsumption,
    segmentEvent: REPORT_DEU_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.agentConsumption,
    segmentEvent: REPORT_AGENT_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.sessionConsumption,
    segmentEvent: REPORT_SESSION_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.mauConsumption,
    segmentEvent: REPORT_REVENUE_CONSUMPTION_OPEN,
  },
  {
    path: billingRoutes.meuConsumption,
    segmentEvent: REPORT_MEU_CONSUMPTION_OPEN,
  },
];

export const billingSubRoutesTracks = [
  {
    path: billingSubRoutes.agentConsumptionMonthly,
    segmentEvent: REPORT_AGENT_CONSUMPTION_LIST_OPEN,
  },
  {
    path: billingSubRoutes.dauConsumptionMonthly,
    segmentEvent: REPORT_DAU_CONSUMPTION_PER_CHATBOT_OPEN,
  },
  {
    patch: billingSubRoutes.deuConsumptionMonthly,
    segmentEvent: REPORT_DEU_CONSUMPTION_PER_CHATBOT_OPEN,
  },
  {
    path: billingSubRoutes.dauConsumptionDaily,
    segmentEvent: REPORT_DAU_CONSUMPTION_PER_DATE_OPEN,
  },
  {
    patch: billingSubRoutes.deuConsumptionDaily,
    segmentEvent: REPORT_DEU_CONSUMPTION_PER_DATE_OPEN,
  },
  {
    path: billingSubRoutes.sessionConsumptionMonthly,
    segmentEvent: REPORT_SESSION_CONSUMPTION_PER_PHONENUMBER_OPEN,
  },
  {
    path: billingSubRoutes.sessionConsumptionDaily,
    segmentEvent: REPORT_SESSION_CONSUMPTION_PER_DATE_OPEN,
  },
  {
    path: billingSubRoutes.mauConsumptionChatbots,
    segmentEvent: REPORT_REVENUE_CONSUMPTION_PER_CHATBOT_OPEN,
  },
  {
    path: billingSubRoutes.meuConsumptionChatbots,
    segmentEvent: REPORT_MEU_CONSUMPTION_PER_CHATBOT_OPEN,
  },
];

export const billingRoutesMetrics = [
  {
    path: billingRoutes.dataConsumption,
    metrics: Object.values(BillingMetrics).map<BillingMetrics>((metric) => metric as BillingMetrics),
    featureToggle: DATA_CONSUMPTION,
  },
  {
    path: billingRoutes.dauConsumption,
    metrics: [BillingMetrics.dailyActiveUser],
    featureToggle: DAILY_ACTIVE_USERS_CONSUMPTION,
  },
  {
    patch: billingRoutes.deuConsumption,
    metrics: [BillingMetrics.dailyEngagedUser],
    featureToggle: DAILY_ENGAGED_USERS_CONSUMPTION,
  },
  {
    path: billingRoutes.agentConsumption,
    metrics: [BillingMetrics.agent],
    featureToggle: AGENT_CONSUMPTION,
  },
  {
    path: billingRoutes.sessionConsumption,
    metrics: [BillingMetrics.session],
    featureToggle: SESSION_CONSUMPTION,
  },
  {
    path: billingRoutes.mauConsumption,
    metrics: [BillingMetrics.monthlyActiveUser, BillingMetrics.exchangedMessage],
    featureToggle: TENANT_CONSUMPTION,
  },
  {
    path: billingRoutes.meuConsumption,
    metrics: [BillingMetrics.monthlyEngagedUser, BillingMetrics.exchangedMessage],
    featureToggle: MONTHLY_ENGAGED_USERS_CONSUMPTION,
  },
  {
    path: billingRoutes.advancedDeuConsumption,
    metrics: [BillingMetrics.prospectConversation, BillingMetrics.userConversation, BillingMetrics.activeMessage, BillingMetrics.dailyEngagedUser],
    featureToggle: ADVANCED_DEU_ENABLED,
    isFeatureToggleByCluster: true,
  },
  {
    path: billingRoutes.aiTokenConsumption,
    metrics: [BillingMetrics.aiToken],
    featureToggle: ADVANCED_DEU_ENABLED,
    isFeatureToggleByCluster: true,
  },
];

export const asideMenuOptions = [
  {
    group: 'asideMenu',
    option: 'dataConsumption',
    icon: 'monitoring',
    featureToggle: [{ key: DATA_CONSUMPTION }],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_OVERVIEW_CONSUMPTION_OPEN,
    metrics: Object.values(BillingMetrics).map<BillingMetrics>((metric) => metric as BillingMetrics),
    path: billingRoutes.dataConsumption,
  },
  {
    group: 'asideMenu',
    option: 'advancedDeuConsumption',
    icon: 'user-engaged',
    featureToggle: [
      {
        key: ADVANCED_DEU_ENABLED, 
        isFeatureToggleByCluster: true,
      },
    ],
    usingAllEnabledMetricsValidationType: true,
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_ADVANCED_DEU_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.prospectConversation, BillingMetrics.userConversation, BillingMetrics.activeMessage, BillingMetrics.dailyEngagedUser],
    path: billingRoutes.advancedDeuConsumption,
  },
  {
    group: 'asideMenu',
    option: 'aiTokenConsumption',
    icon: 'user-sso',
    featureToggle: [ 
      {
        key: ADVANCED_DEU_ENABLED, 
        isFeatureToggleByCluster: true,
      },
      { 
        key: AI_TOKEN_ENABLED,
      },
    ],
    usingAllEnabledMetricsValidationType: true,
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_AI_TOKEN_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.aiToken],
    path: billingRoutes.aiTokenConsumption,
  },
  {
    group: 'asideMenu',
    option: 'dauConsumption',
    icon: 'user-active',
    featureToggle: [{key: DAILY_ACTIVE_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_DAU_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.dailyActiveUser],
    path: billingRoutes.dauConsumption,
  },  
  {
    group: 'asideMenu',
    option: 'deuConsumption',
    icon: 'user-engaged',
    featureToggle: [{key: DAILY_ENGAGED_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_DEU_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.dailyEngagedUser],
    path: billingRoutes.deuConsumption,
  },
  {
    group: 'asideMenu',
    option: 'agentConsumption',
    icon: 'agent',
    featureToggle: [{key: AGENT_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_AGENT_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.agent],
    path: billingRoutes.agentConsumption,
  },
  {
    group: 'asideMenu',
    option: 'sessionConsumption',
    icon: 'message-talk',
    featureToggle: [{key: SESSION_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_SESSION_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.session],
    path: billingRoutes.sessionConsumption,
  },
  {
    group: 'asideMenu',
    option: 'mauConsumption',
    icon: 'user-engaged',
    usingAllEnabledMetricsValidationType: true,
    featureToggle: [{key: TENANT_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_REVENUE_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.monthlyActiveUser, BillingMetrics.exchangedMessage],
    path: billingRoutes.mauConsumption,
  },
  {
    group: 'asideMenu',
    option: 'meuConsumption',
    icon: 'user-engaged',
    usingAllEnabledMetricsValidationType: true,
    featureToggle: [{key: MONTHLY_ENGAGED_USERS_CONSUMPTION}],
    accessPermission: accessPermission.billing,
    segmentEvent: REPORT_MEU_CONSUMPTION_OPEN,
    metrics: [BillingMetrics.monthlyEngagedUser, BillingMetrics.exchangedMessage],
    path: billingRoutes.meuConsumption,
  },
];

const BillingRoutes = () => {
  const { tenant } = useTenant();
  const { common } = useCommon();
  return (
    <BillingConsumptionContextProvider tenant={tenant} common={common}>
      <BillingMetricsContextProvider tenant={tenant}>
        <BillingMetricConfiguration />
      </BillingMetricsContextProvider>
    </BillingConsumptionContextProvider>
  );
};

const BillingMetricConfiguration = () => {
  const { tenant } = useTenant();
  return (
    <DauConsumptionContextProvider tenant={tenant}>
      <DeuConsumptionContextProvider tenant={tenant}>
        <AdvancedDeuConsumptionContextProvider tenant={tenant}>
          <AgentConsumptionContextProvider tenant={tenant}>
            <SessionConsumptionContextProvider tenant={tenant}>
              <MiuConsumptionContextProvider tenant={tenant}>
                <MeuConsumptionContextProvider tenant={tenant}>
                  <AiTokenConsumptionDataContextProvider tenant={tenant}>
                    <BillingPlanUsageConfiguration />
                  </AiTokenConsumptionDataContextProvider>
                </MeuConsumptionContextProvider>
              </MiuConsumptionContextProvider>
            </SessionConsumptionContextProvider>
          </AgentConsumptionContextProvider>
        </AdvancedDeuConsumptionContextProvider>
      </DeuConsumptionContextProvider>
    </DauConsumptionContextProvider>
  );
};

const BillingPlanUsageConfiguration = () => {
  const { dauStatus } = useDauConsumptionContext();
  const { deuStatus } = useDeuConsumptionContext();
  const { agentStatus } = useAgentConsumptionContext();
  const { sessionStatus } = useSessionConsumptionContext();
  const { miuStatus } = useMiuConsumptionContext();
  const { meuStatus } = useMeuConsumptionContext();

  return (
    <BillingPlanUsageContextProvider planStatusArray={[dauStatus, deuStatus, agentStatus, sessionStatus, miuStatus, meuStatus]}>
      <Routes />
    </BillingPlanUsageContextProvider>
  );
};

const Routes = () => {
  const { hasEnabledMetric, isMetricAllowed, allEnabledMetrics } = useBillingMetricsContext();
  return (
    <>
      <PrivateRoute
        path={billingRoutes.revenue}
        component={Revenue}
        hasPermission={() =>
          hasEnabledMetric([
            BillingMetrics.monthlyActiveUser,
            BillingMetrics.monthlyEngagedUser,
            BillingMetrics.exchangedMessage,
          ])
        }
      />
      <PrivateRoute
        path={billingRoutes.dailyActiveUsers}
        component={DailyActiveUsers}
        hasPermission={() => isMetricAllowed(BillingMetrics.dailyActiveUser)}
      />
      <PrivateRoute
        path={billingRoutes.dailyEngagedUsers}
        component={DailyEngagedUsers}
        hasPermission={() => isMetricAllowed(BillingMetrics.dailyEngagedUser)}
      />
      <PrivateRoute
        path={billingRoutes.oldSessionConsumption}
        component={OldSessionConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.session)}
      />
      <PrivateRoute
        path={billingRoutes.oldAgentConsumption}
        component={OldAgentConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.agent)}
      />
      <PrivateRoute
        path={billingRoutes.mauConsumption}
        component={MiuConsumption}
        hasPermission={() =>
          hasEnabledMetric([
            BillingMetrics.monthlyActiveUser,
            BillingMetrics.exchangedMessage,
          ])
        }
      />
      <PrivateRoute
        path={billingRoutes.meuConsumption}
        component={MeuConsumption}
        hasPermission={() =>
          hasEnabledMetric([
            BillingMetrics.monthlyEngagedUser,
            BillingMetrics.exchangedMessage,
          ])
        }
      />
      <PrivateRoute
        path={billingRoutes.dauConsumption}
        component={DauConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.dailyActiveUser)}
      />
      <PrivateRoute
        path={billingRoutes.deuConsumption}
        component={DeuConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.dailyEngagedUser)}
      />
      <PrivateRoute
        path={billingRoutes.sessionConsumption}
        component={SessionConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.session)}
      />
      <PrivateRoute
        path={billingRoutes.agentConsumption}
        component={AgentConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.agent)}
      />

      <PrivateRoute
        path={billingRoutes.dataConsumption}
        component={BillingConsumption}
        hasPermission={() =>
          hasEnabledMetric(Object.values(BillingMetrics).map<BillingMetrics>((metric) => metric as BillingMetrics))
        }
      />
      <PrivateRoute
        path={billingRoutes.advancedDeuConsumption}
        component={AdvancedDeuConsumption}
        hasPermission={() =>
          allEnabledMetrics([
            BillingMetrics.prospectConversation,
            BillingMetrics.userConversation,
            BillingMetrics.activeMessage,
            BillingMetrics.dailyEngagedUser,
          ])
        }
      />
      <PrivateRoute
        path={billingRoutes.aiTokenConsumption}
        component={AiTokenConsumption}
        hasPermission={() => isMetricAllowed(BillingMetrics.aiToken)}
      />
    </>
  );
};

export { BillingRoutes };
